import React from 'react';
import {Alert, Avatar, Box, Button, Paper, Stack, Typography} from '@mui/material';
import {
    areDirectlyConnected,
    buildChartEntityChipPayload,
    buildTraceIndirectConnectionsPrompt,
    listDisjointEntityPairs,
    toggleTraceIndirectSelection,
} from '../utils/disjointChartEntities';

function entityImageSrc(entity) {
    return entity?.imageUrl || entity?.image || '';
}

function avatarInitials(label) {
    const text = String(label || '').trim();
    if (!text) {
        return '?';
    }
    return text.slice(0, 1).toUpperCase();
}

/**
 * In-chat picker: list on-chart entities as full-width rows (same feel as
 * mutation-preview candidate cards), let the user select exactly two, then
 * submit the existing kbGraphPaths / indirect-connection prompt.
 *
 * @param {{
 *   chartEntities?: Array<{ wikidataId: string, label?: string, entityType?: string, imageUrl?: string, image?: string }>,
 *   chartConnections?: Array<{ from: string, to: string }>,
 *   onCancel: () => void,
 *   onConfirm: (prompt: string) => void,
 * }} props
 */
const TraceIndirectConnectionsPicker = ({
    chartEntities = [],
    chartConnections = [],
    onCancel,
    onConfirm,
}) => {
    const [selectedIds, setSelectedIds] = React.useState([]);
    const rootRef = React.useRef(null);

    React.useLayoutEffect(() => {
        rootRef.current?.scrollIntoView({block: 'end', inline: 'nearest'});
    }, []);

    const disjointPairs = React.useMemo(
        () => listDisjointEntityPairs(chartEntities, chartConnections),
        [chartEntities, chartConnections],
    );

    const hasEnoughEntities = chartEntities.length >= 2;
    const hasDisjointPairs = disjointPairs.length > 0;
    const [firstId, secondId] = selectedIds;
    const directlyConnected = Boolean(
        firstId
        && secondId
        && areDirectlyConnected(firstId, secondId, chartConnections),
    );

    const handleToggle = (entityId) => {
        const next = toggleTraceIndirectSelection(selectedIds, entityId);
        setSelectedIds(next);
        if (
            next.length === 2
            && !areDirectlyConnected(next[0], next[1], chartConnections)
        ) {
            onConfirm(buildTraceIndirectConnectionsPrompt({
                fromEntityId: next[0],
                toEntityId: next[1],
                chartEntities,
            }));
        }
    };

    return (
        <Stack ref={rootRef} spacing={1.5} data-testid="trace-indirect-entity-picker">
            <Paper elevation={0} sx={{py: 1}}>
                <Box sx={{fontWeight: 600, fontSize: 14}}>Trace indirect connections</Box>
            </Paper>

            <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                    Choose two people or organizations on this chart that are not already linked.
                </Typography>

                {!hasEnoughEntities ? (
                    <Alert severity="info">
                        Add at least two people or organizations to the chart before tracing indirect connections.
                    </Alert>
                ) : null}

                {hasEnoughEntities && !hasDisjointPairs ? (
                    <Alert severity="info">
                        Everyone on this chart is already directly linked. Add people or organizations
                        without connecting them, or pick a different analysis.
                    </Alert>
                ) : null}

                {hasEnoughEntities && hasDisjointPairs ? (
                    <>
                        <Stack spacing={0.75}>
                            {chartEntities.map((entity) => {
                                const payload = buildChartEntityChipPayload(entity);
                                if (!payload) {
                                    return null;
                                }
                                const selected = selectedIds.includes(entity.wikidataId);
                                const label = payload.label;
                                const imageSrc = entityImageSrc(entity);
                                return (
                                    <Box
                                        key={entity.wikidataId}
                                        component="button"
                                        type="button"
                                        data-testid="trace-indirect-entity-row"
                                        aria-label={`Select ${label}`}
                                        aria-pressed={selected}
                                        onClick={() => handleToggle(entity.wikidataId)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            width: '100%',
                                            px: 1,
                                            py: 0.75,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: selected ? 'grey.200' : 'grey.50',
                                            cursor: 'pointer',
                                            font: 'inherit',
                                            color: 'inherit',
                                            textAlign: 'left',
                                            '&:hover': {
                                                bgcolor: selected ? 'grey.300' : 'grey.100',
                                            },
                                        }}
                                    >
                                        <Avatar
                                            alt=""
                                            src={imageSrc || undefined}
                                            imgProps={imageSrc ? {'data-testid': 'trace-indirect-entity-image'} : undefined}
                                            sx={{width: 28, height: 28, fontSize: 13, flexShrink: 0}}
                                        >
                                            {imageSrc ? null : avatarInitials(label)}
                                        </Avatar>
                                        <Typography
                                            variant="body2"
                                            sx={{fontSize: 12, fontWeight: 500, wordBreak: 'break-word'}}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>

                        {selectedIds.length < 2 ? (
                            <Typography variant="caption" color="text.secondary">
                                {selectedIds.length === 0 ? 'Select 2 people or organizations.' : 'Select 1 more.'}
                            </Typography>
                        ) : null}

                        {directlyConnected ? (
                            <Alert severity="warning">
                                These two are already directly connected on the chart. Pick a pair that is not already linked.
                            </Alert>
                        ) : null}
                    </>
                ) : null}

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={onCancel}>Cancel</Button>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default TraceIndirectConnectionsPicker;
